import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { Box, makeStyles, TextField } from "@material-ui/core";

import { getRoutesList } from "../../store/selectors/routes";
import SearchIcon from "../icons/Search";
import { ROUTE_SEARCH } from "../../constants/strings";
import palette from "../styles/palette";
import { View } from "../Base";
import { parseQuery, stringifyQuery } from "../../utils/query";
import Autocomplete from "../Autocomplete";
import useLocationQueryParams from "../../hooks/useLocationQueryParams";
import { RouteListTabs } from "../../enums/RouteListTabs";

const useStyles = makeStyles(({ typography }) => ({
  wrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    "& .MuiInput-underline:before": {
      borderBottomColor: palette.typography.white.solid,
    },
    "& .MuiInput-underline:hover:before": {
      borderBottomColor: palette.typography.white.solid,
      borderBottom: "1px solid",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: palette.typography.white.solid,
      borderBottom: "1px solid",
    },
  },
  inputRoot: {
    paddingRight: "5px !important",
    paddingBottom: "3px",
    color: palette.typography.white.solid,
    fontFamily: "Roboto",
    fontSize: typography.pxToRem(12),
    lineHeight: typography.pxToRem(14),
    letterSpacing: "-0.02em",
    textTransform: "capitalize",
  },
  clearIndicator: {
    display: "none",
  },
  input: {
    paddingBottom: "3px",
    textOverflow: "ellipsis !important",
    "&::placeholder": {
      color: palette.typography.white.solid,
      opacity: 1,
    },
  },
  icon: {
    maxWidth: typography.pxToRem(16),
    maxHeight: typography.pxToRem(16),
    marginLeft: "7px",
    "& svg": {
      fill: palette.typography.white.solid,
    },
  },
  popupIndicatorOpen: {
    color: "inherit",
    transform: "none",
  },
}));

const RouteSearch = ({ onSelectRoute }) => {
  const styles = useStyles();
  const history = useHistory();
  const routes = useSelector(getRoutesList);
  const { routeId } = useLocationQueryParams();
  const [selectedRoute, setSelectedRoute] = useState(null);

  const options = useMemo(() => {
    return Object.values(routes).reduce((formattedOptions, route) => {
      if (!route.defaultRoute) {
        formattedOptions.push({
          value: route.routeId,
          label: `${route.routeCode} - ${route.driverName || "No Driver"}`,
        });
      }
      return formattedOptions;
    }, []);
  }, [routes]);

  useEffect(() => {
    onSelectRoute(routeId);
  }, []);

  useEffect(() => {
    if (selectedRoute && routeId !== selectedRoute) {
      setSelectedRoute(null);
      onSelectRoute(null);
    }
  }, [routeId, selectedRoute, onSelectRoute]);

  const handleRouteSelect = useCallback(
    (newValue) => {
      const { routeId, ...newQuery } = parseQuery(history.location.search);
      history.push({
        search: stringifyQuery({
          ...newQuery,
          routeId: newValue ? newValue : undefined,
        }),
      });
      setSelectedRoute(newValue);
      onSelectRoute(newValue);
    },
    [history, routes, onSelectRoute]
  );

  const renderInput = useCallback(
    (params) => (
      <TextField
        {...params}
        inputProps={{
          ...params.inputProps,
          className: styles.input,
        }}
        InputLabelProps={{
          ...params.InputLabelProps,
        }}
        variant="standard"
        placeholder={ROUTE_SEARCH}
      />
    ),
    [styles]
  );

  return (
    <Box className={styles.wrapper}>
      <Autocomplete
        onChange={handleRouteSelect}
        value={selectedRoute}
        renderInput={renderInput}
        options={options}
        popperProps={{ placement: "bottom-start" }}
        dataTestId="search-route"
        popupIcon={false}
        clearIcon={false}
        classes={{
          inputRoot: styles.inputRoot,
          popupIndicatorOpen: styles.popupIndicatorOpen,
          clearIndicator: styles.clearIndicator,
        }}
      />
      <View className={styles.icon}>
        <SearchIcon />
      </View>
    </Box>
  );
};

export default memo(RouteSearch);
